import { useEffect, useState } from 'react';
import { Eye, ChevronDown, ChevronRight, Loader2, Pencil, BellOff, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { roleLabel } from '../../lib/viewAs';

const fmt = (s) => { try { return s ? format(parseISO(s), 'dd/MM/yyyy HH:mm') : '—'; } catch { return '—'; } };
const fmtT = (s) => { try { return s ? format(parseISO(s), 'HH:mm:ss') : ''; } catch { return ''; } };
const duration = (a, b) => {
  if (!a) return '—';
  const ms = (b ? new Date(b) : new Date()) - new Date(a);
  const m = Math.max(0, Math.round(ms / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};
const END_LABEL = { exit: 'Exited', expired: 'Timed out', replaced: 'Replaced by a new session', target_unavailable: 'User unavailable' };

function Row({ s }) {
  const [open, setOpen] = useState(false);
  const live = s.live;
  return (
    <div className="rounded-lg border border-white/5 bg-[#1E2530]" data-testid={`impersonation-log-row-${s.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left p-4 flex items-start gap-3" data-imp-safe>
        {open ? <ChevronDown className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" /> : <ChevronRight className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />}
        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_auto] gap-x-6 gap-y-1 items-start">
          <div className="min-w-0">
            <p className="text-sm text-white font-medium truncate">{s.real_name} <span className="text-gray-500 font-normal">viewed as</span> {s.acting_name} <span className="text-gray-500 font-normal">({roleLabel(s)})</span></p>
            <p className="text-xs text-gray-500 truncate">{s.real_email} → {s.acting_email}</p>
          </div>
          <div className="text-xs text-gray-400">
            <p>Started {fmt(s.started_at)}</p>
            <p>{s.ended_at ? `Ended ${fmt(s.ended_at)}` : <span className="text-[#F5A623]">Still running</span>} · {duration(s.started_at, s.ended_at)}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {live && <Badge className="bg-[#F5A623]/20 text-[#F5A623] border-[#F5A623]/30 text-[11px]">Live</Badge>}
            {s.allow_edits || s.edit_reason ? <Badge className="bg-red-500/15 text-red-300 border-red-500/30 text-[11px]"><Pencil className="w-3 h-3 mr-1" /> Edits allowed</Badge> : <Badge className="bg-white/5 text-gray-300 border-white/10 text-[11px]">Read-only</Badge>}
            {s.extensions > 0 && <Badge className="bg-white/5 text-gray-300 border-white/10 text-[11px]"><Clock className="w-3 h-3 mr-1" /> Extended</Badge>}
            {s.end_reason && !live && <Badge className="bg-white/5 text-gray-400 border-white/10 text-[11px]">{END_LABEL[s.end_reason] || s.end_reason}</Badge>}
          </div>
          <div className="text-xs text-gray-400 text-right whitespace-nowrap">
            <p>{s.action_count} action{s.action_count === 1 ? '' : 's'}</p>
            {s.suppressed_count > 0 && <p className="inline-flex items-center gap-1"><BellOff className="w-3 h-3" /> {s.suppressed_count} held back</p>}
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5 px-4 py-3 grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs" data-testid={`impersonation-log-detail-${s.id}`}>
          <div>
            <p className="uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Timeline</p>
            {(s.events || []).map((e, i) => (
              <p key={i} className="text-gray-300"><span className="text-gray-500 tabular-nums">{fmtT(e.at)}</span> · {e.event.replace('_', ' ')}{e.reason ? ` — “${e.reason}”` : ''}</p>
            ))}
            {s.ended_at && <p className="text-gray-300"><span className="text-gray-500 tabular-nums">{fmtT(s.ended_at)}</span> · ended ({END_LABEL[s.end_reason] || s.end_reason})</p>}
          </div>
          <div>
            <p className="uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Changes made ({s.action_count})</p>
            {!s.action_count && <p className="text-gray-500">None — read-only session.</p>}
            {(s.actions || []).slice().reverse().map((a, i) => (
              <p key={i} className="text-gray-300 font-mono truncate" title={a.path}><span className="text-gray-500">{fmtT(a.at)}</span> {a.method} {a.path.replace(/^\/api/, '')}</p>
            ))}
          </div>
          <div>
            <p className="uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Notifications held back ({s.suppressed_count})</p>
            {!s.suppressed_count && <p className="text-gray-500">None.</p>}
            {(s.suppressed || []).slice().reverse().map((n, i) => (
              <p key={i} className="text-gray-300 truncate"><span className="text-gray-500">{fmtT(n.at)}</span> {n.kind === 'email' ? 'Email' : 'Alert'}: {n.summary}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Settings → Admin → Impersonation log: every "view as user" session, who, when, and anything changed. */
export function ImpersonationLogTab({ api }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get('/admin/impersonation/log').then((r) => setRows(r.data || [])).catch(() => { toast.error('Could not load the impersonation log'); setRows([]); });
  }, [api]);
  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="impersonation-log">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F5A623]/10 flex items-center justify-center"><Eye className="w-5 h-5 text-[#F5A623]" /></div>
          <div>
            <CardTitle className="text-xl text-white">Impersonation log</CardTitle>
            <CardDescription className="text-gray-400">Every “view as user” session — who viewed as whom, for how long, and any changes saved while edits were allowed.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {rows === null && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>}
        {rows && !rows.length && <p className="text-sm text-gray-500 text-center py-8" data-testid="impersonation-log-empty">No impersonation sessions yet. Start one from your avatar menu → Admin → Impersonate a user.</p>}
        <div className="space-y-2">{(rows || []).map((s) => <Row key={s.id} s={s} />)}</div>
      </CardContent>
    </Card>
  );
}
